<template>
    <div>
        <div ref="mapContainer" class="min-h-[400px] mobile:min-h-[300px] w-full border border-grey-300 rounded-lg z-0"></div>
    </div>
</template>

<script lang="ts">
/// <reference types="@types/google.maps" />
import { defineComponent, PropType } from 'vue';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.fullscreen';
import 'leaflet.fullscreen/Control.FullScreen.css';
import { Adres } from '../../../../types/dto/Adres';
import { MaatschappelijkAdres, Vestiging } from "../../types";

const createHtmlIcon = (iconClass: string, colorClass: string) => {
    return L.divIcon({
        html: `
            <div class="relative flex items-center justify-center">
                <i class="fas fa-location-pin ${colorClass} text-3xl" style="-webkit-text-stroke: 1.5px white;"></i>
                <i class="fas ${iconClass} absolute text-white text-[14px] mb-2"></i>
            </div>
        `,
        className: 'custom-leaflet-icon', // Verwijder standaard Leaflet styling
        iconSize: [30, 42],
        iconAnchor: [15, 42],
        popupAnchor: [0, -40]
    });
};

const Icons = {
    huidigeZetel: createHtmlIcon('fa-chair-office', 'text-pine-400'),
    vorigeZetel: createHtmlIcon('fa-chair-office', 'text-primary-400'),
    vestiging: createHtmlIcon('fa-store', 'text-pine-400'),
    postbus: createHtmlIcon('fa-envelope', 'text-pine-400')
};

interface MapPoint {
    lat: number;
    lng: number;
    title: string;
    adres: string;
    dateInfo: string;
    icon: L.Icon;
    adresObj: Adres;
}

export default defineComponent({
    name: "locations-map",
    props: {
        zetels: { type: Array as PropType<MaatschappelijkAdres[]>, default: () => [] },
        vestigingen: { type: Array as PropType<Vestiging[]>, default: () => [] },
        bijkantoren: { type: Array as PropType<Vestiging[]>, default: () => [] },
        postbusAdressen: { type: Array as PropType<Adres[]>, default: () => [] }
    },
    data() {
        return {
            map: null as L.Map | null,
            markers: [] as L.Marker[]
        };
    },
    watch: {
        zetels: { handler: 'refreshMap', deep: true },
        vestigingen: { handler: 'refreshMap', deep: true },
        bijkantoren: { handler: 'refreshMap', deep: true },
        postbusAdressen: { handler: 'refreshMap', deep: true }
    },
    methods: {
        async refreshMap() {
            const points = await this.collectMapPoints();
            this.initMap(points);
        },

        async collectMapPoints(): Promise<MapPoint[]> {
            const points: MapPoint[] = [];

            const add = (adres: Adres, title: string, icon: L.DivIcon, begin?: number | null, einde?: number | null) => {
                const dateStr = [
                    begin ? `${this.translate('sinds')}: ${Date.fromHansDate(begin).format()}` : '',
                    einde ? `${this.translate('tot')}: ${Date.fromHansDate(einde).format()}` : ''
                ].filter(Boolean).join(' <br> ');

                points.push({
                    lat: adres.coordinates?.latitude ?? -1,
                    lng: adres.coordinates?.longitude ?? -1,
                    title: title,
                    adres: `${adres.lijn1}, ${adres.lijn2}`,
                    dateInfo: dateStr,
                    icon: icon,
                    adresObj: adres
                });
            };

            [...this.vestigingen, ...this.bijkantoren].forEach(v => {
                add(v.adres, this.translate('news_article_type_vestiging'), Icons.vestiging, v.begin);
            });

            this.postbusAdressen.forEach(p => {
                add(p, 'Postbus adressen', Icons.postbus);
            });

            this.zetels.forEach(z => {
                const isHuidig = !z.einde || z.einde === 0;
                add(
                    z.adres,
                    this.translate('maatschappelijke_zetel'),
                    isHuidig ? Icons.huidigeZetel : Icons.vorigeZetel,
                    z.begin,
                    z.einde
                );
            });

            for (const point of points) {
                if (point.lat === -1 || point.lat === 0) {
                    const coords = await this.geocodeAddress(point.adresObj);
                    if (coords) {
                        point.lat = coords.lat;
                        point.lng = coords.lng;
                    }
                }
            }
            return points;
        },

        async geocodeAddress(adres: Adres): Promise<{lat: number, lng: number} | null> {
            try {
                await this.waitForGoogle();
                const { Geocoder } = await google.maps.importLibrary("geocoding") as google.maps.GeocodingLibrary;
                const geocoder = new Geocoder();

                const addressString = `${adres.lijn1}, ${adres.lijn2}`;
                const response = await geocoder.geocode({ address: addressString });

                if (response.results && response.results[0]) {
                    return {
                        lat: response.results[0].geometry.location.lat(),
                        lng: response.results[0].geometry.location.lng()
                    };
                }
            } catch (e) {
                console.error("Geocoding failed for", adres, e);
            }
            return null;
        },

        waitForGoogle() {
            return new Promise((resolve) => {
                if (typeof google !== 'undefined' && google.maps?.importLibrary) return resolve(true);
                const interval = setInterval(() => {
                    if (typeof google !== 'undefined' && google.maps?.importLibrary) {
                        clearInterval(interval);
                        resolve(true);
                    }
                }, 100);
            });
        },

        initMap(points: MapPoint[]) {
            const container = this.$refs.mapContainer as HTMLElement;
            if (!container) return;

            if (!this.map) {
                this.map = L.map(container, {
                    fullscreenControl: true,
                    fullscreenControlOptions: { position: 'topright',
                        forceSeparateButton:true }
                });

                L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                    attribution: '&copy; OpenStreetMap'
                }).addTo(this.map);
            }

            this.markers.forEach(m => m.remove());
            this.markers = [];

            const validPoints = points.filter(p => p.lat !== -1 && p.lat !== 0);

            if (validPoints.length === 0) {
                this.map.setView([50.8503, 4.3517], 8); 
                return;
            }

            const bounds = L.latLngBounds([]);

            validPoints.forEach(p => {
                let priority = 0;

                if (p.icon === Icons.huidigeZetel) {
                    priority = 1000;
                } else if (p.icon === Icons.vorigeZetel) {
                    priority = 500;  
                } else {
                    priority = 0;    
                }
                
                const marker = L.marker([p.lat, p.lng], { 
                    icon: p.icon,
                    zIndexOffset: priority })
                    .bindPopup(`
    <div class="grid grid-cols-2 gap-5 w-[380px]"> <img src='${p.adresObj.streetviewUrl}' class="h-32 object-cover" alt="Streetview"/>
        <div>
            <div class="text-base font-semibold mb-1">${p.title}</div>
            <span class="text-grey-700">${p.adres}</span>
            ${p.dateInfo ? `<hr class="my-2 border-grey-200"/><span class="sm text-grey-700">${p.dateInfo}</span>` : ''}
        </div>
    </div>
`, {
                        minWidth: 380,
                        maxWidth: 420
                    })
                    .addTo(this.map!);

                this.markers.push(marker);
                bounds.extend([p.lat, p.lng]);
            });

            this.map.fitBounds(bounds, { padding: [50, 50] });
        }
    },
    mounted() {
        this.refreshMap();
    }
});
</script>

<style>

.leaflet-pane, .leaflet-top, .leaflet-bottom {
    z-index: 30;
}
.leaflet-popup-content {
    margin: 16px !important;
}
</style>